'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';
import { Eye, EyeOff, Heart, Mail, Lock, User, MapPin, Users, Zap, Trophy, ChevronRight, Star, Globe, Sparkles, Shield } from 'lucide-react';
import Image from 'next/image';
import CityDatesLogo from '@/components/ui/CityDatesLogo';
import RealtimePlayerCount from '@/components/RealtimePlayerCount';
import { useLanguage } from '@/contexts/LanguageContext';

export default function LoginPage() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [isDay, setIsDay] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const { signIn, signUp } = useAuth();
  const router = useRouter();
  const supabase = createClient();
  const { language, setLanguage, t } = useLanguage();

  const FEATURES = [
    {
      icon: <MapPin size={18} />,
      color: '#f472b6',
      bg: 'rgba(244,114,182,0.12)',
      border: 'rgba(244,114,182,0.25)',
      label: language === 'en' ? 'Real Locations' : 'Реални локации',
      desc: language === 'en' ? 'Cafes, parks, galleries' : 'Кафенета, паркове, галерии'
    },
    {
      icon: <Users size={18} />,
      color: '#60a5fa',
      bg: 'rgba(96,165,250,0.12)',
      border: 'rgba(96,165,250,0.25)',
      label: language === 'en' ? 'Real Players' : 'Реални играчи',
      desc: language === 'en' ? 'Meet people around you' : 'Срещай хора около теб',
      showCount: true
    },
    {
      icon: <Zap size={18} />,
      color: '#fbbf24',
      bg: 'rgba(251,191,36,0.12)',
      border: 'rgba(251,191,36,0.25)',
      label: language === 'en' ? 'Quests' : 'Куестове',
      desc: language === 'en' ? 'Daily challenges' : 'Дневни предизвикателства'
    },
    {
      icon: <Trophy size={18} />,
      color: '#34d399',
      bg: 'rgba(52,211,153,0.12)',
      border: 'rgba(52,211,153,0.25)',
      label: language === 'en' ? 'Achievements' : 'Постижения',
      desc: language === 'en' ? 'Earn rewards and levels' : 'Печели награди и нива'
    },
    {
      icon: <Sparkles size={18} />,
      color: '#a78bfa',
      bg: 'rgba(167,139,250,0.12)',
      border: 'rgba(167,139,250,0.25)',
      label: language === 'en' ? 'AI Scenarios' : 'ИИ Сценарии',
      desc: language === 'en' ? 'Unique story moments' : 'Уникални моменти от историята'
    },
    {
      icon: <Shield size={18} />,
      color: '#fb923c',
      bg: 'rgba(251,146,60,0.12)',
      border: 'rgba(251,146,60,0.25)',
      label: language === 'en' ? 'Safe Dating' : 'Безопасни срещи',
      desc: language === 'en' ? 'Verified profiles only' : 'Само верифицирани профили'
    }
  ];

  const TESTIMONIALS = language === 'en' ? [
    { text: 'Finally something different and real.', name: 'Ivana, 24', city: 'Varna', initial: 'I' },
    { text: 'I found friends in my neighborhood!', name: 'Maria, 26', city: 'Sofia', initial: 'M' },
    { text: 'Amazing concept for dating.', name: 'Georgi, 31', city: 'Plovdiv', initial: 'G' },
    { text: 'Met my partner here after just two weeks!', name: 'Petya, 28', city: 'Burgas', initial: 'P' },
    { text: 'The city map feature is genius — I discovered new places.', name: 'Dimitar, 33', city: 'Sofia', initial: 'D' },
    { text: 'Real people, real conversations. Refreshing!', name: 'Elena, 27', city: 'Varna', initial: 'E' }
  ] : [
    { text: 'Накрая нещо различно и реално.', name: 'Ивана, 24', city: 'Варна', initial: 'И' },
    { text: 'Намерих приятели в моя квартал!', name: 'Мария, 26', city: 'София', initial: 'М' },
    { text: 'Страхотна концепция за запознанства.', name: 'Георги, 31', city: 'Пловдив', initial: 'Г' },
    { text: 'Запознах се с партньора си само след две седмици!', name: 'Петя, 28', city: 'Бургас', initial: 'П' },
    { text: 'Картата на града е гениална — открих нови места.', name: 'Димитър, 33', city: 'София', initial: 'Д' },
    { text: 'Истински хора, истински разговори. Освежаващо!', name: 'Елена, 27', city: 'Варна', initial: 'Е' }
  ];

  useEffect(() => {
    setMounted(true);
    const hour = new Date().getHours();
    setIsDay(hour >= 6 && hour < 20);
    const t = setInterval(() => setTestimonialIdx((i) => (i + 1) % TESTIMONIALS.length), 4000);

    return () => clearInterval(t);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    if (!email || !password) {
      setFormError(t.auth.fillAllFields);
      toast.error(t.auth.fillAllFields);
      return;
    }
    setLoading(true);
    try {
      if (mode === 'login') {
        await signIn(email, password);
        setFormSuccess(t.auth.welcomeBack);
        toast.success(t.auth.welcomeBack);
        router.push('/city-map-screen');
      } else {
        if (!fullName.trim()) {
          setFormError(t.auth.enterName);
          toast.error(t.auth.enterName);
          setLoading(false);
          return;
        }
        const data = await signUp(email, password, { fullName });
        if (data?.user && !data?.session) {
          const msg = language === 'en' ?'Account created! Check your email for confirmation, then log in.' :'Акаунтът е създаден! Провери имейла си за потвърждение, след което влез.';
          setFormSuccess(msg);
          toast.success(msg);
        } else {
          setFormSuccess(t.auth.accountCreated);
          toast.success(t.auth.accountCreated);
          router.push('/character-creation-screen');
        }
      }
    } catch (err: any) {
      const msg = err?.message || t.auth.loginError;
      let userMsg = msg;
      if (msg.includes('Invalid login credentials')) userMsg = t.auth.wrongCredentials;
      else if (msg.includes('User already registered')) userMsg = t.auth.emailExists;
      else if (msg.includes('Email not confirmed')) userMsg = language === 'en' ? 'Email not confirmed. Check your inbox.' : 'Имейлът не е потвърден. Провери пощата си.';
      else if (msg.includes('Password should be at least')) userMsg = language === 'en' ? 'Password must be at least 6 characters.' : 'Паролата трябва да е поне 6 символа.';
      setFormError(userMsg);
      toast.error(userMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback` }
      });
      if (error) throw error;
    } catch (err: any) {
      toast.error(t.auth.googleError + (err?.message || ''));
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'bg' ? 'en' : 'bg');
  };

  const testimonial = TESTIMONIALS[testimonialIdx];

  return (
    <div className="min-h-screen flex overflow-hidden relative" style={{ background: '#0a0318' }}>
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        {mounted && (
          <Image
            src={isDay
              ? "/assets/images/ChatGPT_Image_Apr_26_2026_09_08_20_AM-1777183792029.png" :"/assets/images/ChatGPT_Image_Apr_25_2026_11_48_13_PM-1777151010301.png"
            }
            alt="CityDates background"
            fill
            style={{ objectFit: 'cover', objectPosition: 'center top' }}
            priority
          />
        )}
        {/* Dark overlay */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(5,2,18,0.92) 0%, rgba(15,5,40,0.80) 45%, rgba(5,2,18,0.94) 100%)'
        }} />
        {/* Right fade */}
        <div className="absolute top-0 right-0 bottom-0 w-1/2" style={{
          background: 'linear-gradient(to left, rgba(5,2,18,0.75) 0%, transparent 100%)'
        }} />
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-48" style={{
          background: 'linear-gradient(to top, rgba(5,2,18,0.98) 0%, transparent 100%)'
        }} />
        {/* Subtle pink glow top-left */}
        <div className="absolute top-0 left-0 w-96 h-96 pointer-events-none" style={{
          background: 'radial-gradient(circle at 20% 20%, rgba(214,51,132,0.18) 0%, transparent 70%)'
        }} />
      </div>

      {/* Stars — night only */}
      {mounted && !isDay && Array.from({ length: 40 }).map((_, i) => (
        <div key={`star-${i}`} className="absolute rounded-full pointer-events-none z-0"
          style={{
            width: `${[1, 1, 2][i % 3]}px`,
            height: `${[1, 1, 2][i % 3]}px`,
            top: `${(i * 17 + 5) % 75}%`,
            left: `${(i * 29 + 7) % 85}%`,
            background: i % 3 === 0 ? 'rgba(240,180,200,0.9)' : 'rgba(200,210,255,0.7)',
            opacity: 0.25 + (i % 4) * 0.12,
            animation: `twinkle ${2.5 + (i % 4) * 0.7}s ease-in-out infinite`,
            animationDelay: `${(i % 6) * 0.5}s`
          }}
        />
      ))}

      {/* ── MAIN LAYOUT ── */}
      <div className="relative z-10 flex w-full min-h-screen">

        {/* ── LEFT PANEL ── */}
        <div className="hidden lg:flex flex-col flex-1 px-6 xl:px-10 py-6 min-w-0 justify-between">

          {/* Top: Logo + tagline + language switcher */}
          <div className="flex items-start justify-between">
            <div>
              <CityDatesLogo width={280} height={146} />
            </div>
            {/* Language switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 mt-2"
              style={{
                background: 'rgba(244,114,182,0.1)',
                border: '1px solid rgba(244,114,182,0.3)',
                color: '#f472b6',
                backdropFilter: 'blur(12px)'
              }}
              title={language === 'bg' ? 'Switch to English' : 'Смени на Български'}
            >
              <Globe size={15} />
              <span className="text-xs font-bold tracking-wider">
                {language === 'bg' ? 'EN' : 'BG'}
              </span>
            </button>
          </div>

          {/* Middle: Heading + description + features */}
          <div className="flex flex-col gap-5 flex-1 justify-start" style={{ maxWidth: '600px' }}>
            {/* Heading */}
            <div>
              <h1 className="font-extrabold leading-[1.1]" style={{
                fontSize: 'clamp(2.4rem, 3.5vw, 3.4rem)',
                color: '#f8f0f5',
                textShadow: '0 2px 24px rgba(0,0,0,0.6)'
              }}>
                {language === 'en' ? 'Meet real people' : 'Запознай се с реални хора'}
              </h1>
 
            </div>

            {/* Description */}
            <p className="text-base leading-relaxed font-medium" style={{
              color: 'rgba(230,190,215,0.82)',
              maxWidth: '100%',
              letterSpacing: '0.01em'
            }}>
              {language === 'en' ?'A dating role-playing game in real locations – cafes, parks, galleries. Build a character, explore the city and find real connections.' :'Ролева игра за срещи в реални локации – кафенета, паркове, галерии. Изгради персонаж, изследвай града и намери истински връзки.'}
            </p>

            {/* Feature grid — 3 per row */}
            <div className="grid grid-cols-3 gap-3" style={{ maxWidth: '100%' }}>
              {FEATURES.map((f, i) => (
                <div key={i}
                  className="rounded-2xl p-3.5 flex items-center gap-3 transition-all duration-200 hover:scale-[1.02]"
                  style={{
                    background: f.bg,
                    border: `1px solid ${f.border}`,
                    backdropFilter: 'blur(12px)'
                  }}>
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${f.color}20`, color: f.color }}>
                    {f.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-1.5 flex-wrap" style={{ color: '#f0e0ec' }}>
                      {f.label}
                      {f.showCount && (
                        <RealtimePlayerCount city="София" className="opacity-90" />
                      )}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: 'rgba(220,170,200,0.55)' }}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom: Testimonial */}
          {mounted && (
            <div className="rounded-2xl p-4 border" style={{
              background: 'rgba(8,3,22,0.65)',
              borderColor: 'rgba(244,114,182,0.2)',
              backdropFilter: 'blur(16px)',
              maxWidth: '520px',
              marginBottom: '8px'
            }}>
              <div className="flex gap-0.5 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={12} fill="#f472b6" color="#f472b6" />
                ))}
              </div>
              <p className="text-sm italic leading-relaxed" style={{ color: 'rgba(240,210,225,0.88)' }}>
                &ldquo;{testimonial.text}&rdquo;
              </p>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: 'linear-gradient(135deg, #be185d, #f472b6)', color: 'white' }}>
                    {testimonial.initial}
                  </div>
                  <div>
                    <div className="text-xs font-semibold" style={{ color: 'rgba(240,200,220,0.9)' }}>{testimonial.name}</div>
                    <div className="text-xs" style={{ color: 'rgba(200,160,190,0.55)' }}>{testimonial.city}</div>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  {TESTIMONIALS.map((_, i) => (
                    <div key={i} className="rounded-full transition-all duration-300"
                      style={{
                        width: i === testimonialIdx ? '20px' : '6px',
                        height: '6px',
                        background: i === testimonialIdx ? '#f472b6' : 'rgba(244,114,182,0.28)'
                      }} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="flex-shrink-0 flex items-center justify-center p-4 lg:p-8 xl:p-10 w-full lg:w-auto">
          {/* Mobile logo */}
          <div className="lg:hidden absolute top-5 left-5 z-20">
            <CityDatesLogo width={200} height={104} />
          </div>
          {/* Mobile language switcher */}
          <div className="lg:hidden absolute top-5 right-5 z-20">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl transition-all duration-200"
              style={{
                background: 'rgba(244,114,182,0.12)',
                border: '1px solid rgba(244,114,182,0.3)',
                color: '#f472b6'
              }}
            >
              <Globe size={14} />
              <span className="text-xs font-bold">{language === 'bg' ? 'EN' : 'BG'}</span>
            </button>
          </div>

          <div className="relative w-full" style={{ maxWidth: '420px', marginLeft: '-10px' }}>
            {/* Glow ring */}
            <div className="absolute inset-0 rounded-3xl pointer-events-none" style={{
              boxShadow: '0 0 0 1px rgba(244,114,182,0.4), 0 0 40px rgba(214,51,132,0.28), 0 0 80px rgba(180,40,120,0.14)',
              borderRadius: '28px'
            }} />

            {/* Card */}
            <div className="rounded-3xl relative" style={{
              background: 'rgba(8,3,24,0.90)',
              border: '1.5px solid rgba(244,114,182,0.35)',
              backdropFilter: 'blur(48px)',
              borderRadius: '28px',
              padding: '32px 30px'
            }}>

              {/* Mobile: tagline */}
              <div className="lg:hidden mb-5 mt-8">
              </div>

              {/* Card header */}
              <div className="mb-6">
                <h2 className="text-2xl font-extrabold" style={{ color: '#f8f0f5' }}>
                  {mode === 'login'
                    ? (language === 'en' ? 'Enter the game' : 'Влез в играта')
                    : (language === 'en' ? 'Create account' : 'Създай акаунт')}
                </h2>
                <p className="text-sm mt-1.5" style={{ color: 'rgba(210,160,190,0.65)' }}>
                  {mode === 'login'
                    ? (language === 'en' ? 'Continue your adventure in CityDates' : 'Продължи своето приключение в CityDates')
                    : (language === 'en' ? 'Free · Only 2 minutes' : 'Безплатно · Само 2 минути')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Feedback messages */}
                {formError && (
                  <div className="rounded-xl px-4 py-3 text-sm font-medium" style={{
                    background: 'rgba(220,38,38,0.14)',
                    border: '1px solid rgba(220,38,38,0.35)',
                    color: '#fca5a5'
                  }}>
                    ⚠️ {formError}
                  </div>
                )}
                {formSuccess && (
                  <div className="rounded-xl px-4 py-3 text-sm font-medium" style={{
                    background: 'rgba(34,197,94,0.14)',
                    border: '1px solid rgba(34,197,94,0.35)',
                    color: '#86efac'
                  }}>
                    ✅ {formSuccess}
                  </div>
                )}

                {mode === 'register' && (
                  <div className="relative">
                    <User size={15} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgba(210,160,190,0.5)' }} />
                    <input
                      type="text"
                      placeholder={t.auth.yourName}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm outline-none transition-all focus:border-pink-500/60"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(180,80,160,0.28)',
                        color: '#f0e0ec',
                        caretColor: '#f472b6'
                      }}
                      autoComplete="name"
                    />
                  </div>
                )}

                <div className="relative">
                  <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgba(210,160,190,0.5)' }} />
                  <input
                    type="email"
                    placeholder={t.auth.email}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(180,80,160,0.28)',
                      color: '#f0e0ec',
                      caretColor: '#f472b6'
                    }}
                    autoComplete="email"
                  />
                </div>

                <div className="relative">
                  <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgba(210,160,190,0.5)' }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t.auth.password}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-11 pr-12 py-3.5 rounded-xl text-sm outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(180,80,160,0.28)',
                      color: '#f0e0ec',
                      caretColor: '#f472b6'
                    }}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: 'rgba(210,160,190,0.5)' }}>
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-xl flex items-center justify-center gap-2.5 text-base font-bold text-white transition-all active:scale-[0.98] mt-1"
                  style={{
                    background: loading
                      ? 'rgba(190,24,93,0.35)'
                      : 'linear-gradient(135deg, #be185d 0%, #f472b6 100%)',
                    boxShadow: loading ? 'none' : '0 6px 32px rgba(190,24,93,0.45)',
                    marginLeft: '-10px'
                  }}>
                  {loading ? (
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : (
                    <>
                      <Heart size={17} fill="white" />
                      <span>
                        {mode === 'login'
                          ? (language === 'en' ? 'Enter the game' : 'Влез в играта')
                          : (language === 'en' ? 'Create free account' : 'Създай акаунт')}
                      </span>
                      <ChevronRight size={17} className="opacity-70" />
                    </>
                  )}
                </button>
              </form>

              {/* Mode switcher */}
              <div className="flex rounded-xl overflow-hidden mt-5 mb-1" style={{ border: '1px solid rgba(180,80,160,0.22)' }}>
                {(['login', 'register'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => { setMode(m); setFormError(''); setFormSuccess(''); }}
                    className="flex-1 py-2.5 text-sm font-semibold transition-all duration-200"
                    style={{
                      background: mode === m ? 'linear-gradient(135deg, #be185d, #f472b6)' : 'transparent',
                      color: mode === m ? 'white' : 'rgba(210,160,190,0.55)'
                    }}>
                    {m === 'login' ? t.auth.login : t.auth.register}
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px" style={{ background: 'rgba(180,80,160,0.2)' }} />
                <span className="text-xs font-medium" style={{ color: 'rgba(200,150,185,0.45)' }}>
                  {language === 'en' ? 'or continue with' : 'или продължи с'}
                </span>
                <div className="flex-1 h-px" style={{ background: 'rgba(180,80,160,0.2)' }} />
              </div>

              {/* Google */}
              <button
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl text-sm font-semibold transition-all duration-200 hover:bg-white/10 active:scale-[0.98]"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(240,220,232,0.88)'
                }}>
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t.auth.continueWithGoogle}
              </button>

            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.6); }
        }
        input::placeholder { color: rgba(200,150,185,0.4); }
        input:focus { border-color: rgba(244,114,182,0.55) !important; outline: none; }
      `}</style>
    </div>
  );
}